import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import BpRadio from '../../../components/layout/BpRadio';
import CustomizedProgressBars from '../../../components/layout/CustomizedProgressBar';
import ConfirmModal from '../../../components/layout/ConfirmModal';
import { updateUserMembership } from "../../../redux/actions/user";
import {ReactComponent as CalendarIcon} from '../../../assets/svg/calendar.svg';

import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useNavigate } from "react-router-dom";
import { useTheme } from '@mui/material/styles';


export default function Membership({ userId }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { clientDetailData } = useSelector(state => ({
    clientDetailData: state.user.clientDetailData,
  }));

  const daysRemaining = moment(clientDetailData.expireDate).diff(moment(), 'days');
  const defaultExpireDate = clientDetailData.membership < 2 ? moment().add(30, 'days') : moment().add(1, 'years');

  const [modalVisible, setModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [membership, setMembership] = useState(clientDetailData.membership < 2 ? "trial" : "pro");
  const [expireDate, setExpireDate] = useState(defaultExpireDate);

  function getDaysRemainingText() {
    return moment(clientDetailData.expireDate).isBefore(moment()) ?
      'Expired' :
      daysRemaining + ' Day' + (daysRemaining > 1 ? 's ' : ' ') + 'Remaining';
  }
  function getDaysRemainingPercent() {
    if (moment(clientDetailData.expireDate).isBefore(moment())) {
      return 100;
    }
    const trialPeriod = moment(clientDetailData.expireDate).diff(moment(clientDetailData.expireResetDate), 'days');
    const pastPeriod = moment().diff(moment(clientDetailData.expireResetDate), 'days');
    return pastPeriod / trialPeriod * 100;
  }
  function getExpiresOnDateText() {
    return (moment(clientDetailData.expireDate).isBefore(moment()) ? "Expired" : "Expires") + " on " + moment(clientDetailData.expireDate).format('D MMMM YYYY');
  }

  
  const onCloseModal = () => setModalVisible(false);
  const onCloseConfirmModal = () => setConfirmModalVisible(false);

  const onSubmitHandler = (data) => {
    setModalVisible(true);
  };

  const onSaveChanges = () => {
    onCloseModal();
    let _membership;
    if (membership === 'pro') {
      _membership = 2;
    } else {
      _membership = moment(expireDate).isAfter(moment()) ? 0 : 1;
    }
    const data = {
      userId,
      membership: _membership,
      expireDate,
    };
    dispatch(updateUserMembership(data));
    setTimeout(() => setConfirmModalVisible(true), 1000);
  }

  return (
    <>
      <Grid>
        <Typography sx={{ fontWeight: 600, fontSize: '1.5rem' }}>Membership Settings</Typography>
      </Grid>
      <Divider sx={{ mt: 2 }} />
      <Grid container alignItems="center" sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Membership Expiry</Typography>
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Remaining period before account deactivates</Typography>
        </Grid>
        <Grid item container xs={12} md sx={{ mt: { xs: 1, md: 0 } }}>
          <Grid
            item
            xs={6}
            sx={{ color: theme.palette.mode === 'light' ? '#3f4254' : '#e0e0e0', fontWeight: 600, fontSize: '0.9rem' }}>
            {clientDetailData.membership < 2 ? "Trial" : "Pro"} Plan
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ color: theme.palette.mode === 'light' ? '#3f4254' : '#e0e0e0', fontWeight: 600, fontSize: '0.9rem', textAlign: 'right' }}>
            {getDaysRemainingText()}
          </Grid>
          <Grid xs={12}>
            <CustomizedProgressBars value={getDaysRemainingPercent()} />
          </Grid>
          <Typography sx={{ color: '#7E8299', fontWeight: 600, fontSize: '0.87rem', mt: 0.5 }}>{getExpiresOnDateText()}</Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="center" sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Set Membership Plan</Typography>
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Modify existing membership plan.</Typography>
        </Grid>
        <Grid item container xs={12} md sx={{ mt: { xs: 1, md: 0 } }}>
          <RadioGroup
            row
            defaultValue="trial"
            aria-labelledby="demo-customized-radios"
            name="customized-radios"
            value={membership}
            onChange={(e) => {
              setExpireDate(e.target.value === 'trial' ? moment().add(30, 'days') : moment().add(1, 'years'));
              setMembership(e.target.value);
            }}>
            <FormControlLabel value="trial" control={<BpRadio />} label="Trial" />
            <FormControlLabel value="pro" control={<BpRadio />} label="Pro" />
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid container alignItems="center" sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Set Expiry Date</Typography>
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Modify existing membership expiry.</Typography>
        </Grid>
        <Grid item container xs={12} md sx={{ mt: { xs: 1, md: 0 } }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              renderInput={(props) => {
                props.InputProps = {
                  ...props.InputProps,
                  disableUnderline: true,
                  style: {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }
                };
                return (
                  <TextField
                    variant="standard"
                    fullWidth
                    sx={{ mt: 1, svg: { color: theme.palette.mode === 'light' ? '#a1a5B7' : '#565674' } }}
                    {...props}
                  />
                );
              }}
              defaultValue={defaultExpireDate}
              minDate={clientDetailData.expireResetDate}
              value={expireDate}
              onChange={setExpireDate}
              InputAdornmentProps={{ position: 'start' }}
              components={{ OpenPickerIcon: CalendarIcon }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2 }} />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          onClick={() => navigate('/superadmin')}
          size="large"
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
            color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '6px',
            padding: '11px 20px',
            "&:hover": {
              backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212E48',
              color: '#009ef7',
            }
          }}>
          Cancel    
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onSubmitHandler}
          sx={{ marginInlineStart: 1, color: '#fff', fontWeight: 600, textTransform: 'none' }}>
          Save Changes
        </Button>
      </Box>
      <ConfirmModal
        visible={modalVisible}
        onClose={onCloseModal}
        symbol="!"
        title="Are you sure you want to change above settings?"
        titleYes="Yes"
        titleNo="No"
        onClickYes={onSaveChanges}
        onClickNo={onCloseModal}
      />
      <ConfirmModal
        visible={confirmModalVisible}
        onClose={onCloseConfirmModal}
        symbol="&#10004;"
        title="You have updated above settings!"
        titleNo="OK"
        onClickNo={onCloseConfirmModal}
      />
    </>
  );
}
